/**
 * DailyOfferPopup — Shown at most once per day to signed-in users.
 * Surfaces a single admin-curated partner offer. Stay 5s (any action) to earn +2 pts.
 */
import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Copy, ExternalLink, Sparkles, Check } from "lucide-react";
import {
  useDailyOffer,
  reportDailyOfferAction,
  markShownToday,
  type DailyOffer,
} from "@/hooks/useDailyOffer";
import { copyToClipboard } from "@/utils/clipboard";
import { toast } from "@/hooks/use-toast";
import { acquirePopup, releasePopup, cancelPopup, POPUP_PRIORITIES } from "@/lib/popupQueue";

const POPUP_ID = "daily-offer";

const REWARD_THRESHOLD_MS = 5000;
const TICK_MS = 100;
export const POINTS_REFRESH_EVENT = "points-refresh";

export function DailyOfferPopup() {
  const { offer, isOpen, dismiss } = useDailyOffer();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [pointsAwarded, setPointsAwarded] = useState(0);
  const openedAtRef = useRef<number | null>(null);
  const actionRef = useRef<"copy_code" | "open_site" | "dismiss" | "timeout">(
    "dismiss",
  );
  const reportedRef = useRef(false);

  useEffect(() => {
    if (isOpen && offer) {
      let cancelled = false;
      acquirePopup(POPUP_ID, POPUP_PRIORITIES.dailyOffer).then(() => {
        if (cancelled) {
          releasePopup(POPUP_ID);
          return;
        }
        setOpen(true);
        openedAtRef.current = Date.now();
        markShownToday();
        actionRef.current = "dismiss";
        reportedRef.current = false;
        setCopied(false);
        setElapsed(0);
        setPointsAwarded(0);
      });
      return () => {
        cancelled = true;
        cancelPopup(POPUP_ID);
      };
    }
  }, [isOpen, offer]);

  // Tick progress while open
  useEffect(() => {
    if (!open || elapsed >= REWARD_THRESHOLD_MS) return;
    const t = setTimeout(() => {
      setElapsed((e) => Math.min(e + TICK_MS, REWARD_THRESHOLD_MS));
    }, TICK_MS);
    return () => clearTimeout(t);
  }, [open, elapsed]);

  // Mark timeout reached
  useEffect(() => {
    if (elapsed >= REWARD_THRESHOLD_MS && actionRef.current === "dismiss") {
      actionRef.current = "timeout";
    }
  }, [elapsed]);

  const close = async (finalAction?: typeof actionRef.current) => {
    if (finalAction) actionRef.current = finalAction;
    const dwell = openedAtRef.current ? Date.now() - openedAtRef.current : 0;
    setOpen(false);
    releasePopup(POPUP_ID);
    dismiss();
    if (offer && !reportedRef.current) {
      reportedRef.current = true;
      const res = await reportDailyOfferAction({
        offer_id: offer.id,
        offer_source: offer.source,
        dwell_ms: dwell,
        action: actionRef.current,
        selection_reason: offer.selection_reason,
      });
      if (res.pointsAwarded > 0) {
        setPointsAwarded(res.pointsAwarded);
        toast({
          title: `+${res.pointsAwarded} points added`,
          description: "Thanks for checking today's partner offer.",
        });
        // Tell every usePoints() instance to refetch
        window.dispatchEvent(new CustomEvent(POINTS_REFRESH_EVENT));
      }
    }
  };

  const handleCopy = async (o: DailyOffer) => {
    if (!o.code) return;
    const ok = await copyToClipboard(o.code);
    if (ok) {
      setCopied(true);
      actionRef.current = "copy_code";
      toast({ title: "Code copied", description: o.code });
    } else {
      toast({ title: "Couldn't copy", variant: "destructive" });
    }
  };

  const handleOpenSite = (o: DailyOffer) => {
    actionRef.current = "open_site";
    if (o.external_url) {
      window.open(o.external_url, "_blank", "noopener,noreferrer");
    }
  };

  if (!offer) return null;

  const reached = elapsed >= REWARD_THRESHOLD_MS;
  const pct = Math.round((elapsed / REWARD_THRESHOLD_MS) * 100);
  const secondsLeft = Math.max(0, Math.ceil((REWARD_THRESHOLD_MS - elapsed) / 1000));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <Badge variant="secondary" className="text-xs">
              Today's partner offer
            </Badge>
          </div>
          <DialogTitle className="text-xl pt-2">{offer.title}</DialogTitle>
          {offer.sponsor_name && (
            <DialogDescription className="flex items-center gap-2">
              {offer.sponsor_logo_url && (
                <img
                  src={offer.sponsor_logo_url}
                  alt=""
                  className="h-5 w-5 rounded object-contain"
                />
              )}
              <span>from {offer.sponsor_name}</span>
            </DialogDescription>
          )}
        </DialogHeader>

        {offer.image_url && (
          <img
            src={offer.image_url}
            alt={offer.title}
            className="w-full h-32 object-cover rounded-md"
          />
        )}

        {offer.description && (
          <p className="text-sm text-muted-foreground">{offer.description}</p>
        )}

        {offer.code && (
          <div className="rounded-md border border-dashed bg-muted/40 p-3">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              Code
            </div>
            <div className="flex items-center justify-between gap-2">
              <code className="font-mono text-sm break-all">{offer.code}</code>
              <Button
                size="sm"
                variant={copied ? "secondary" : "default"}
                onClick={() => handleCopy(offer)}
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5 mr-1" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Reward progress */}
        <div className="space-y-1.5 pt-1">
          <div className="flex items-center justify-between text-xs">
            <span
              className={
                reached
                  ? "text-primary font-medium flex items-center gap-1"
                  : "text-muted-foreground"
              }
            >
              {reached ? (
                <>
                  <Sparkles className="h-3.5 w-3.5" />
                  {pointsAwarded > 0
                    ? `+${pointsAwarded} points added`
                    : "+2 points unlocked"}
                </>
              ) : (
                `Stay ${secondsLeft}s to earn +2 points`
              )}
            </span>
            <span className="text-muted-foreground tabular-nums">{pct}%</span>
          </div>
          <Progress value={pct} className="h-1.5" />
        </div>

        <div className="flex flex-col sm:flex-row gap-2 pt-1">
          {offer.external_url && (
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => handleOpenSite(offer)}
            >
              <ExternalLink className="h-4 w-4 mr-2" /> Visit partner
            </Button>
          )}
          <Button variant="ghost" className="flex-1" onClick={() => close()}>
            Close
          </Button>
        </div>

        {offer.valid_until && (
          <p className="text-[10px] text-muted-foreground text-center">
            Valid until {new Date(offer.valid_until).toLocaleDateString()}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
